import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { partsAPI, exportAPI, importAPI } from "../lib/api";
import { compressImage } from "../lib/imageUtils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, Package, Hash, Edit, Trash2, AlertTriangle, Download, Upload, FileSpreadsheet, Camera, X } from "lucide-react";

const Parts = () => {
  const { user } = useAuth();
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewPhotoDialog, setViewPhotoDialog] = useState({ open: false, photo: null, title: "" });
  const [formData, setFormData] = useState({
    name: "",
    part_number: "",
    quantity: 0,
    unit_price: 0,
    description: "",
    photos: [],
  });

  const canManage = user?.role === "admin" || user?.role === "manager";
  const canExport = user?.role === "admin" || user?.role === "manager";
  const canImport = user?.role === "admin" || user?.role === "manager";

  useEffect(() => {
    fetchParts();
  }, []);

  const fetchParts = async () => {
    try {
      const response = await partsAPI.getAll();
      setParts(response.data);
    } catch (error) {
      toast.error("Failed to fetch parts");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      quantity: parseInt(formData.quantity),
      unit_price: parseFloat(formData.unit_price),
      photos: formData.photos || [],
    };
    try {
      if (editingPart) {
        await partsAPI.update(editingPart.id, submitData);
        toast.success("Part updated");
      } else {
        await partsAPI.create(submitData);
        toast.success("Part added");
      }
      setDialogOpen(false);
      setEditingPart(null);
      setFormData({ name: "", part_number: "", quantity: 0, unit_price: 0, description: "", photos: [] });
      fetchParts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (part) => {
    setEditingPart(part);
    setFormData({
      name: part.name,
      part_number: part.part_number,
      quantity: part.quantity,
      unit_price: part.unit_price,
      description: part.description || "",
      photos: part.photos || [],
    });
    setDialogOpen(true);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressed = await compressImage(file, 400);
      setFormData((prev) => ({ 
        ...prev, 
        photos: [...(prev.photos || []), compressed]
      }));
      toast.success("Photo added");
    } catch (error) {
      toast.error("Failed to upload photo");
    }
    e.target.value = "";
  };

  const handleRemovePhoto = (index) => {
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const openPhotoViewer = (photo, title) => {
    setViewPhotoDialog({ open: true, photo, title });
  };

  const handleDelete = async (partId) => {
    try {
      await partsAPI.delete(partId);
      toast.success("Part deleted");
      fetchParts();
    } catch (error) {
      toast.error("Failed to delete part");
    }
  };

  const handleExport = async () => {
    try {
      toast.info("Preparing export...");
      await exportAPI.download("parts");
      toast.success("Export downloaded");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      toast.info("Downloading template...");
      await importAPI.downloadTemplate("parts");
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const response = await importAPI.upload("parts", file);
      const { imported, skipped, errors } = response.data;
      
      if (imported > 0) {
        toast.success(`Imported ${imported} parts successfully`);
      }
      if (skipped > 0) {
        toast.warning(`Skipped ${skipped} rows`);
      }
      if (errors && errors.length > 0) {
        errors.slice(0, 3).forEach(err => toast.error(err));
      }
      
      setImportDialogOpen(false);
      fetchParts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const filteredParts = parts.filter(
    (p) =>
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.part_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="parts-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Inventory
          </h1>
          <p className="text-slate-500">{filteredParts.length} parts in stock</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canExport && (
            <Button variant="outline" onClick={handleExport} className="rounded-sm" data-testid="export-parts-btn">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
          {canImport && (
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm" data-testid="import-parts-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl font-bold uppercase">
                    Import Parts
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-sm border border-slate-200">
                    <h4 className="font-semibold text-sm uppercase tracking-wide mb-2 flex items-center gap-2">
                      <FileSpreadsheet className="w-4 h-4" />
                      Step 1: Download Template
                    </h4>
                    <p className="text-sm text-slate-600 mb-3">
                      Download the Excel template with the correct column format.
                    </p>
                    <Button
                      variant="outline"
                      onClick={handleDownloadTemplate}
                      className="w-full rounded-sm"
                      data-testid="download-parts-template-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-sm border border-slate-200">
                    <h4 className="font-semibold text-sm uppercase tracking-wide mb-2 flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Step 2: Upload Filled Template
                    </h4>
                    <p className="text-sm text-slate-600 mb-3">
                      Fill in your parts data and upload the Excel file.
                    </p>
                    <label className="block">
                      <Input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleImport}
                        disabled={importing}
                        className="rounded-sm cursor-pointer"
                        data-testid="parts-import-file"
                      />
                    </label>
                    {importing && (
                      <p className="text-sm text-amber-600 mt-2 flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                        Importing...
                      </p>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 space-y-1">
                    <p><strong>Required columns:</strong> name, part_number</p>
                    <p><strong>Optional columns:</strong> quantity, unit_price, description</p>
                    <p>Duplicate part numbers will be skipped.</p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
          {canManage && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setEditingPart(null);
                setFormData({ name: "", part_number: "", quantity: 0, unit_price: 0, description: "", photos: [] });
              }
            }}>
              <DialogTrigger asChild>
                <Button
                  className="bg-blue-500 text-black font-bold uppercase tracking-wider hover:bg-blue-600 rounded-sm border-2 border-transparent hover:border-black"
                  data-testid="add-part-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Part
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl font-bold uppercase">
                    {editingPart ? "Edit Part" : "Add Part"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Hydraulic Filter"
                      className="rounded-sm"
                      required
                      data-testid="part-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">Part Number *</Label>
                    <Input
                      value={formData.part_number}
                      onChange={(e) => setFormData({ ...formData, part_number: e.target.value })}
                      placeholder="e.g., HF-320D-001"
                      className="rounded-sm"
                      required
                      data-testid="part-number"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="font-semibold uppercase text-xs tracking-wide">Quantity</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.quantity}
                        onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                        className="rounded-sm"
                        data-testid="part-quantity"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-semibold uppercase text-xs tracking-wide">Unit Price ($)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.unit_price}
                        onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                        className="rounded-sm"
                        data-testid="part-price"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Part description..."
                      className="rounded-sm"
                      data-testid="part-description"
                    />
                  </div>

                  {/* Photos Section */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      Part Photos
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {(formData.photos || []).map((photo, index) => (
                        <div key={index} className="relative">
                          <img
                            src={photo}
                            alt={`Photo ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-sm border border-slate-200 cursor-pointer hover:border-blue-500"
                            onClick={() => openPhotoViewer(photo, `Part Photo ${index + 1}`)}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(index)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <label className="flex items-center justify-center w-20 h-20 border-2 border-dashed border-slate-300 rounded-sm cursor-pointer hover:border-blue-500 hover:bg-yellow-50/50 transition-colors">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                          data-testid="part-photo-upload"
                        />
                        <div className="text-center">
                          <Plus className="w-6 h-6 text-slate-400 mx-auto" />
                        </div>
                      </label>
                    </div>
                    <p className="text-xs text-slate-400">Add photos of the part (auto-compressed)</p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-sm">
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-blue-500 text-black font-bold uppercase hover:bg-blue-600 rounded-sm"
                      data-testid="part-submit"
                    >
                      {editingPart ? "Update" : "Add"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search parts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 rounded-sm"
          data-testid="parts-search"
        />
      </div>

      {/* Parts Grid */}
      {filteredParts.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No parts found</p>
            {canManage && (
              <Button
                onClick={() => setDialogOpen(true)}
                className="mt-4 bg-blue-500 text-black font-bold uppercase hover:bg-blue-600 rounded-sm"
              >
                Add First Part
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredParts.map((part) => (
            <Card
              key={part.id}
              className={`border-slate-200 hover:border-slate-400 transition-colors ${
                part.quantity < 10 ? "border-amber-300 bg-amber-50/50" : ""
              }`}
            >
              <CardContent className="p-6">
                <div className="flex gap-4">
                  {/* Part Photo */}
                  {part.photos && part.photos.length > 0 ? (
                    <div 
                      className="w-20 h-20 flex-shrink-0 cursor-pointer"
                      onClick={() => openPhotoViewer(part.photos[0], `${part.name} Photo`)}
                    >
                      <img
                        src={part.photos[0]}
                        alt={part.name}
                        className="w-full h-full object-cover rounded-sm border border-slate-200 hover:border-blue-500 transition-colors"
                      />
                    </div>
                  ) : (
                    <div className="w-20 h-20 flex-shrink-0 bg-slate-100 rounded-sm flex items-center justify-center border border-slate-200">
                      <Package className="w-8 h-8 text-slate-300" />
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-heading font-bold text-base text-slate-900 line-clamp-1">{part.name}</h3>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Hash className="w-3 h-3" />
                          {part.part_number}
                        </p>
                      </div>
                      {canManage && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(part)}
                            className="h-7 w-7 text-slate-500 hover:text-slate-900"
                            data-testid={`edit-part-${part.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {user?.role === "admin" && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-slate-500 hover:text-red-600"
                                  data-testid={`delete-part-${part.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Part?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                This will permanently delete {part.name}. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(part.id)}
                                className="bg-red-500 hover:bg-red-600"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Stock</p>
                      <div className="flex items-center gap-1">
                        {part.quantity < 10 && (
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                        )}
                        <span className={`font-heading font-bold text-xl ${
                          part.quantity < 10 ? "text-amber-600" : "text-slate-900"
                        }`}>
                          {part.quantity}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Price</p>
                      <span className="font-heading font-bold text-xl text-slate-900">
                        ${part.unit_price.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
                {part.description && (
                  <p className="text-sm text-slate-500 mt-3 pt-3 border-t border-slate-100 line-clamp-2">
                    {part.description}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Photo Viewer Dialog */}
      <Dialog open={viewPhotoDialog.open} onOpenChange={(open) => setViewPhotoDialog({ ...viewPhotoDialog, open })}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold uppercase">{viewPhotoDialog.title}</DialogTitle>
          </DialogHeader>
          {viewPhotoDialog.photo && (
            <img
              src={viewPhotoDialog.photo}
              alt={viewPhotoDialog.title}
              className="w-full h-auto max-h-[70vh] object-contain rounded-sm"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Parts;
